/*
 * roleSelection.ts
 *
 * Deciding which role an operator acts as, from the contents of their JWT.
 *
 * WHY THIS IS A SEPARATE FILE
 * ---------------------------
 * This decision has produced two regressions already, both of the same shape:
 * a branch that was correct for the accounts the author had in mind and wrong
 * for the majority case. It is small, pure, and entirely about edge cases, so
 * it belongs somewhere `tests/RoleSelection.smoke.ts` can exercise it under
 * plain ts-node without booting Angular.
 *
 * THE TWO SOURCES OF ROLE, AND WHICH WINS
 * ---------------------------------------
 *   1. `cognito:groups` - entries shaped `<role>-<roleId>`, e.g.
 *      `organization-4021`. A user may hold SEVERAL, which is what makes the
 *      role picker necessary. This is authoritative membership.
 *   2. `custom:role` + `custom:roleId` - a single role carried as custom
 *      attributes on the account.
 *
 * **Groups win whenever there are any.** The custom attributes are only
 * consulted when the group list yields nothing usable. That ordering is
 * deliberate and is a security property, not a convenience: groups are what
 * revocation removes, so if a user's groups have been taken away we must not
 * fall back to a stale `custom:role` and hand back what was just revoked.
 *
 * MOST ACCOUNTS HAVE NO GROUPS
 * ----------------------------
 * This is the fact the previous implementation missed. The common account has
 * an empty `cognito:groups` and a `custom:role` / `custom:roleId` pair. Such an
 * account is completely set up - it simply has no ALTERNATIVE role to switch
 * to. Treating "no group options" as "this account is broken" mislabels the
 * majority of users.
 */

export interface RoleOption {
  role: string;
  roleId: string;
}

/**
 * What the caller should do about role selection.
 *
 * - `KEEP`              - the role already selected is still valid; do nothing.
 * - `SELECT`            - select this role without asking.
 * - `CHOOSE`            - more than one option; send the operator to the picker.
 * - `SETUP_INCOMPLETE`  - no groups and no custom attributes. The account
 *                         exists and is signed in but has nothing behind it.
 */
export type RoleSelectionIntent =
  | { kind: 'KEEP' }
  | { kind: 'SELECT', role: string, roleId: string, source: 'GROUP' | 'CUSTOM_ATTRIBUTE' }
  | { kind: 'CHOOSE' }
  | { kind: 'SETUP_INCOMPLETE' };

/** `cognito:groups` as an array, whatever the claim actually contained. */
export function readCognitoGroups(jwtDecoded: any): string[] {
  if (jwtDecoded && Array.isArray(jwtDecoded['cognito:groups'])) {
    return jwtDecoded['cognito:groups'];
  }
  return [];
}

/**
 * Turn `cognito:groups` into role options.
 *
 * Only entries that split into exactly two parts on '-' are usable; anything
 * else is not a `<role>-<roleId>` pair and is ignored rather than guessed at.
 *
 * De-duplicated by `role|roleId`, preserving first-seen order: Cognito should
 * not emit duplicates, but if it does they would inflate the count past 1 and
 * drive the operator into a picker offering the same choice twice.
 */
export function parseRoleOptions(cognitoGroups: string[]): RoleOption[] {
  const options: RoleOption[] = [];
  const seen: { [key: string]: boolean } = {};
  for (const group of (cognitoGroups || [])) {
    if (typeof group !== 'string') { continue; }
    const parts = group.split('-');
    if (parts.length !== 2) { continue; }
    const key = parts[0] + '|' + parts[1];
    if (seen[key]) { continue; }
    seen[key] = true;
    options.push({ role: parts[0], roleId: parts[1] });
  }
  return options;
}

/** The `custom:role` / `custom:roleId` pair, or null when not usable. */
export function readCustomAttributeRole(jwtDecoded: any): RoleOption | null {
  if (!jwtDecoded) { return null; }
  const role = jwtDecoded['custom:role'];
  const roleId = jwtDecoded['custom:roleId'];
  if (role === undefined || role === null || roleId === undefined || roleId === null) {
    return null;
  }
  const roleText = String(role).trim();
  // roleId is numeric in practice, so it is stringified rather than
  // truthiness-tested - an id of 0 is still an id.
  const roleIdText = String(roleId).trim();
  if (roleText.length === 0 || roleIdText.length === 0) { return null; }
  return { role: roleText, roleId: roleIdText };
}

/**
 * Decide what to do about role selection for this token.
 *
 * @param jwtDecoded        the decoded id token
 * @param currentRole       `StartupService.selectedRole` before this token
 * @param currentRoleId     `StartupService.selectedRoleId` before this token
 */
export function resolveRoleSelection(
  jwtDecoded: any,
  currentRole: string,
  currentRoleId: string
): RoleSelectionIntent {
  const cognitoGroups = readCognitoGroups(jwtDecoded);
  const roleOptions = parseRoleOptions(cognitoGroups);
  const customRole = readCustomAttributeRole(jwtDecoded);

  // Keep the operator where they are if the role they already hold is still
  // backed by this token. Checked against groups first, then the custom
  // attributes, so a token refresh does not bounce a group-less user out of
  // the role they are working in.
  if (currentRole && currentRoleId) {
    const stillInGroups = cognitoGroups.indexOf(currentRole + '-' + currentRoleId) !== -1;
    const matchesCustom = !!customRole
      && roleOptions.length === 0
      && customRole.role === currentRole
      && customRole.roleId === currentRoleId;
    if (stillInGroups || matchesCustom) {
      return { kind: 'KEEP' };
    }
  }

  if (roleOptions.length === 1) {
    return { kind: 'SELECT', role: roleOptions[0].role, roleId: roleOptions[0].roleId, source: 'GROUP' };
  }
  if (roleOptions.length > 1) {
    return { kind: 'CHOOSE' };
  }

  // No usable groups. This is the ordinary account, not a broken one.
  if (customRole) {
    return { kind: 'SELECT', role: customRole.role, roleId: customRole.roleId, source: 'CUSTOM_ATTRIBUTE' };
  }

  // Nothing anywhere: no groups, no custom attributes. The account is real and
  // authenticated but has nothing behind it - the state a migrating user is
  // left in when PostConfirmation did not complete.
  return { kind: 'SETUP_INCOMPLETE' };
}
